import { vehicleDisplay } from './models/shared/customerVehiclesV1Model';
import dayjs from "dayjs";
import { URGENCY_CFG } from "./pipeline";

// Muted rather than saturated, so a table full of red/green doesn't fight
// the eye — still clearly distinguishable, just not alarm-colored.
const PART_PRICE_COLORS = { hasPrice: "#2e7d32", noPrice: "#a13d3d" };

const partPriceLabel = (p) => `${p.part_name}: ${p.selling_price != null ? `AED ${Number(p.selling_price).toLocaleString()}` : "N/A"}`;

const partsSortedNoPriceFirst = (r) => [...(r.query_parts || [])].sort((a, b) => (a.selling_price != null) - (b.selling_price != null));

// Column definitions shared between QuerysetV4's PDF export
// (generateQv4QueriesPdfBase64) and Excel export (generateQv4QueriesExcel)
// in misc_functions.js, plus the per-user "Customize PDF/Excel columns"
// picker in UserSettingsModal.js — kept in one place so both export formats
// and the settings UI never drift out of sync on what a column actually is.
// Order here matches QuerysetTable.js's own column order where a column
// overlaps with what's already visible in the table (query_id, created_at,
// agent, vehicle, parts, customer_name, phone, status); the rest are extra
// querysetV2 fields that exist in the dataset but aren't shown as their own
// table column today.
export const EXPORT_COLUMNS = [
  { key: "customer_reference", label: "Customer ref", getValue: r => r.customer_reference || "—" },
  {
    key: "query_id",
    label: "Query ID",
    getValue: (r) => String(r.query_id ?? "N/A"),
  },
  {
    key: "created_at",
    label: "Created",
    getValue: (r) => {
      const d = r.created_at ? dayjs(r.created_at) : null;
      return d?.isValid() ? d.format("DD-MM-YYYY h:mm A") : "N/A";
    },
  },
  {
    key: "agent",
    label: "Agent",
    getValue: (r) => {
      const assignee = r.assigned_user;
      return assignee
        ? (assignee.name ? `${assignee.name}${assignee.surname ? " " + assignee.surname : ""}` : assignee.username)
        : "N/A";
    },
  },
  {
    key: "vehicle",
    label: "Vehicle",
    getValue: (r) => {
      const v = r.vehicle;
      return vehicleDisplay(v) || "N/A";
    },
  },
  {
    key: "parts",
    label: "Parts",
    getValue: (r) => (r.query_parts || []).map(p => p.part_name).join(", ") || "N/A",
  },
  {
    key: "part_prices",
    label: "Part Prices",
    // No-price parts first (Array.prototype.sort is stable, so relative
    // order within each group is preserved) — surfaces the parts still
    // needing a price without scanning the whole list.
    getValue: (r) => partsSortedNoPriceFirst(r).map(partPriceLabel).join(", ") || "N/A",
    // PDF-only variant: getValue's comma string is what Excel gets (the
    // xlsx package here, SheetJS Community Edition, can't render pdfmake's
    // per-line colors or newlines — Pro-only styling features), but pdfmake
    // can render one colored line per part via a `stack` cell. Colors are
    // deliberately muted, not saturated red/green, to stay readable against
    // the table's alternating row fillColor.
    getPdfValue: (r) => {
      const parts = partsSortedNoPriceFirst(r);
      if (!parts.length) return "N/A";
      return {
        stack: parts.map(p => ({
          text: partPriceLabel(p),
          color: p.selling_price != null ? PART_PRICE_COLORS.hasPrice : PART_PRICE_COLORS.noPrice,
        })),
      };
    },
  },
  {
    key: "customer_name",
    label: "Customer Name",
    getValue: (r) => r.customer?.customer_name || r.customer_name || "N/A",
  },
  {
    key: "phone",
    label: "Phone",
    getValue: (r) => r.customer?.customer_mobile || r.customer_mobile || "N/A",
  },
  {
    key: "status",
    label: "Status",
    getValue: (r) => r.status || "N/A",
  },
  // Extra fields — present on every querysetV2 row but not currently
  // rendered as their own column in the table.
  {
    key: "urgency",
    label: "Urgency",
    getValue: (r) => URGENCY_CFG[r.urgency]?.label || "N/A",
  },
  {
    key: "remarks",
    label: "Remarks",
    getValue: (r) => r.remarks || "N/A",
  },
  {
    key: "customer_real_name",
    label: "Customer Real Name",
    getValue: (r) => r.customer?.customer_real_name || "N/A",
  },
  {
    key: "postponed_until",
    label: "Postponed Until",
    getValue: (r) => {
      const d = r.postponed_until ? dayjs(r.postponed_until) : null;
      return d?.isValid() ? d.format("DD-MM-YYYY h:mm A") : "N/A";
    },
  },
];

const DEFAULT_KEYS = EXPORT_COLUMNS.map(c => c.key);
const storageKey = (userId) => `qv4_export_columns_${userId || "anon"}`;
const migrationsKey = (userId) => `qv4_export_columns_migrations_${userId || "anon"}`;

// Columns added after this per-user picker already had users with saved
// preferences — each gets force-enabled exactly once (below) so existing
// users don't have to know to go re-visit settings to discover new export
// data. After that one-time nudge, the user's own toggle is respected as
// normal, including turning it back off. Add new keys here as new columns
// are introduced.
const FORCE_ENABLE_ONCE_KEYS = ["part_prices"];

// All columns on by default (matches the export's pre-existing fixed
// column set) so an unvisited settings screen changes nothing.
export function loadExportColumnKeys(userId) {
  const valid = new Set(DEFAULT_KEYS);
  let keys;
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey(userId)));
    keys = Array.isArray(stored) && stored.length ? stored.filter(k => valid.has(k)) : null;
  } catch {
    keys = null;
  }
  if (!keys || !keys.length) return DEFAULT_KEYS;

  let migrated;
  try {
    migrated = JSON.parse(localStorage.getItem(migrationsKey(userId))) || [];
    if (!Array.isArray(migrated)) migrated = [];
  } catch {
    migrated = [];
  }
  const pending = FORCE_ENABLE_ONCE_KEYS.filter(k => valid.has(k) && !migrated.includes(k));
  if (pending.length) {
    const merged = Array.from(new Set([...keys, ...pending]));
    saveExportColumnKeys(userId, merged);
    localStorage.setItem(migrationsKey(userId), JSON.stringify([...migrated, ...pending]));
    return merged;
  }
  return keys;
}

export function saveExportColumnKeys(userId, keys) {
  localStorage.setItem(storageKey(userId), JSON.stringify(keys));
}

export function getEnabledExportColumns(userId) {
  const keys = new Set(loadExportColumnKeys(userId));
  return EXPORT_COLUMNS.filter(c => keys.has(c.key));
}
